/**
 * Vistas de trabajo P1 (Lista · Kanban · Gantt · Planificación) — modelo de
 * dominio compartido. Todas las vistas leen el MISMO `Task[]` (sin copias).
 *
 * Módulo puro: sin React ni red (solo tipos de `workApi` borrados en runtime).
 */
import type { TaskPriority, TaskRow, TaskStatus } from '../../stores/workApi';

export type { TaskPriority, TaskStatus };

/** ONE_TIME (Puntual) | PERIODIC (Periódica) | RECURRING (Recurrente). */
export type TaskType = 'ONE_TIME' | 'PERIODIC' | 'RECURRING';
/** Frecuencia de la definición periódica (si taskType = PERIODIC). */
export type TaskFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'CUSTOM';

export const TASK_STATUSES: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'IN_REVIEW', 'DONE', 'CANCELLED'];
export const TASK_PRIORITIES: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
export const TASK_TYPES: TaskType[] = ['ONE_TIME', 'PERIODIC', 'RECURRING'];
export const TASK_FREQUENCIES: TaskFrequency[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUAL', 'CUSTOM'];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  TODO: 'Pendiente', IN_PROGRESS: 'En curso', BLOCKED: 'Bloqueada', IN_REVIEW: 'En revisión', DONE: 'Completada', CANCELLED: 'Cancelada',
};
export const PRIORITY_LABELS: Record<TaskPriority, string> = { LOW: 'Baja', MEDIUM: 'Media', HIGH: 'Alta', URGENT: 'Urgente' };
export const TASK_TYPE_LABELS: Record<TaskType, string> = { ONE_TIME: 'Puntual', PERIODIC: 'Periódica', RECURRING: 'Recurrente' };
export const FREQUENCY_LABELS: Record<TaskFrequency, string> = {
  DAILY: 'Diaria', WEEKLY: 'Semanal', MONTHLY: 'Mensual', QUARTERLY: 'Trimestral', ANNUAL: 'Anual', CUSTOM: 'Personalizada',
};
/** Sin estimación nunca se inventa una duración (REQ-046c). */
export const DURACION_PENDIENTE = 'DURACIÓN PENDIENTE';

/** Colores de prioridad (barras Gantt + badges Kanban). */
export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  LOW: '#64748b', MEDIUM: '#3b82f6', HIGH: '#F59E0B', URGENT: '#F43F5E',
};
/** Color de estado (icono de fila Gantt). */
export const STATUS_COLORS: Record<TaskStatus, string> = {
  TODO: '#9ca3af', IN_PROGRESS: '#3b82f6', BLOCKED: '#F43F5E', IN_REVIEW: '#D946EF', DONE: '#10B981', CANCELLED: '#64748b',
};

/** Regla de tarea periódica (la regla NO ejecuta nada por sí sola). */
export interface PeriodicRule {
  frequency: TaskFrequency;
  /** AAAA-MM-DD. */
  startDate: string | null;
  /** AAAA-MM-DD. */
  endDate: string | null;
}

export interface RecurringLineOption {
  id: string;
  name: string;
}

/** Aristas de dependencia entre tareas (para las flechas del Gantt). */
export interface TaskDependency {
  taskId: string;
  dependsOnTaskId: string;
}

/** Modelo de vista de tarea compartido por Lista · Kanban · Gantt · Planificación. */
export interface Task {
  id: string;
  taskCode: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignee: { id: string; name: string } | null;
  processId: string | null;
  processName: string | null;
  projectId: string | null;
  projectName: string | null;
  /** Hierarchy V2 nivel 6 (primero en sacrificarse en UI). */
  description?: string | null;
  /** Hierarchy V2 nivel 5: ubicación/lugar real. Legacy = null. */
  location?: string | null;
  dueDate: string | null;
  estimatedMinutes: number | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  taskType: TaskType;
  periodicRule?: PeriodicRule | null;
  recurringLineId?: string | null;
}

/* ── Helpers puros ─────────────────────────────────────────── */

const MS_DAY = 86_400_000;
const MS_MIN = 60_000;

const ms = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso).getTime();
  return Number.isNaN(t) ? null : t;
};

export const taskStartMs = (t: Task): number | null => ms(t.plannedStart);

/** plannedEnd → plannedStart + estimatedMinutes → null (sin plan). */
export function taskEndMs(t: Task): number | null {
  const start = ms(t.plannedStart);
  const end = ms(t.plannedEnd);
  if (end !== null) return end;
  if (start !== null && t.estimatedMinutes != null) return start + t.estimatedMinutes * MS_MIN;
  return start;
}

/** Duración en ms de la barra; null si la tarea no tiene planificación. */
export function taskDurationMs(t: Task): number | null {
  const start = taskStartMs(t);
  const end = taskEndMs(t);
  if (start === null || end === null) return null;
  return Math.max(end - start, 0);
}

/** Tarea sin ninguna fecha planificada → fila «No planificada». */
export const isUnplanned = (t: Task): boolean => taskStartMs(t) === null;

/** RBAC Kanban: solo las tareas propias se mueven; SUPERADMIN puede mover todas. */
export function canMoveTask(task: Task, currentUserId: string | null | undefined, isSuperAdmin: boolean | undefined): boolean {
  if (isSuperAdmin) return true;
  return !!task.assignee && !!currentUserId && task.assignee.id === currentUserId;
}

/** «15/10/2026» — fechas de día (AAAA-MM-DD) o instantes ISO. */
export function fmtDate(iso: string | null): string {
  const t = ms(iso);
  if (t === null) return '—';
  return new Date(t).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

/** Duración legible: «90 min» / «2 h 30 min» / «DURACIÓN PENDIENTE». */
export function fmtMinutes(min: number | null): string {
  if (min == null) return DURACION_PENDIENTE;
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * Adaptador `TaskRow` (API) → `Task` (vistas). Tolera campos extendidos del
 * backend (taskType / plannedStart…) sin inventar datos cuando faltan.
 */
/**
 * Label textual jerárquico V2 para contextos no-JSX (options de select, tooltips,
 * labels de menú): Project → Area/Process → CODE · Title. Omite limpiamente los
 * niveles ausentes. No inventa datos.
 */
export function taskHierarchyLabel(t: {
  projectName?: string | null;
  processName?: string | null;
  taskCode?: string | null;
  title: string;
}): string {
  const parts: string[] = [];
  if (t.projectName) parts.push(t.projectName);
  if (t.processName) parts.push(t.processName);
  parts.push([t.taskCode, t.title].filter(Boolean).join(' · '));
  return parts.join(' → ');
}

export function taskFromRow(row: TaskRow): Task {
  const r = row as TaskRow & {
    taskType?: string; processId?: string | null; estimatedMinutes?: number | null;
    plannedStart?: string | null; plannedEnd?: string | null; recurringLineId?: string | null;
  };
  return {
    id: r.id,
    taskCode: r.taskCode,
    title: r.title,
    status: r.status,
    priority: r.priority,
    assignee: r.assignee ? { id: r.assignee.id, name: r.assignee.name } : null,
    processId: r.processId ?? r.process?.id ?? null,
    processName: r.process?.name ?? null,
    projectId: r.project.id,
    projectName: r.project.name,
    description: (r as { description?: string | null }).description ?? null,
    location: (r as { location?: string | null }).location ?? null,
    dueDate: r.dueDate,
    estimatedMinutes: r.estimatedMinutes ?? r.timing?.estimatedMinutes ?? null,
    plannedStart: r.plannedStart ?? r.timing?.plannedStart ?? null,
    plannedEnd: r.plannedEnd ?? r.timing?.plannedEnd ?? null,
    taskType: (TASK_TYPES as string[]).includes(r.taskType ?? '') ? (r.taskType as TaskType) : 'ONE_TIME',
    recurringLineId: r.recurringLineId ?? null,
  };
}

export const MS_DAY_VALUE = MS_DAY;
